import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useControllableValue } from '@/ui/kit'
import {
  PAGINATION_ELLIPSIS,
  PAGINATION_ITEM,
  PAGINATION_ITEM_ACTIVE,
  PAGINATION_NEXT,
  PAGINATION_PREV,
  PAGINATION_ROOT,
  PAGINATION_SELECT,
  PAGINATION_TOTAL,
} from '@/ui/shared/paginationStyles'
import { cn } from './internal/utils'
import { FOCUS_RING } from './internal/presets'

/**
 * antd's page window: first, last, current +/- 2, with ellipses between.
 * Returns page numbers and the string '...' markers.
 */
export const buildPageItems = (current, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1)
  }

  const pages = new Set([1, totalPages])
  for (let page = current - 2; page <= current + 2; page += 1) {
    if (page > 1 && page < totalPages) pages.add(page)
  }

  const sorted = [...pages].sort((a, b) => a - b)
  const items = []
  sorted.forEach((page, index) => {
    if (index > 0 && page - sorted[index - 1] > 1) items.push(`ellipsis-${page}`)
    items.push(page)
  })
  return items
}

/** Nomad: ghost (inactive) / outline (active); skin via .ui-pagination-* */
const itemClasses = (isActive) =>
  cn(PAGINATION_ITEM, FOCUS_RING, isActive && PAGINATION_ITEM_ACTIVE)

/**
 * antd Pagination. Supports current/defaultCurrent, pageSize/defaultPageSize,
 * total, onChange, onShowSizeChange, showSizeChanger, showTotal, showQuickJumper,
 * simple, size and disabled.
 */
const Pagination = ({
  current,
  defaultCurrent = 1,
  pageSize,
  defaultPageSize = 10,
  total = 0,
  onChange,
  onShowSizeChange,
  showSizeChanger = false,
  pageSizeOptions = [10, 20, 50, 100],
  showTotal,
  showQuickJumper = false,
  simple = false,
  size = 'default',
  hideOnSinglePage = false,
  disabled = false,
  align,
  className,
  style,
  ...rest
}) => {
  const { t } = useTranslation()
  const [activePage, setActivePage] = useControllableValue(current, defaultCurrent)
  const [activeSize, setActiveSize] = useControllableValue(pageSize, defaultPageSize)

  const totalPages = Math.max(1, Math.ceil(total / activeSize))
  const page = Math.min(Math.max(1, activePage), totalPages)

  if (hideOnSinglePage && totalPages <= 1) return null

  const goTo = (next) => {
    const target = Math.min(Math.max(1, next), totalPages)
    if (target === page) return
    setActivePage(target)
    onChange?.(target, activeSize)
  }

  const changeSize = (nextSize) => {
    setActiveSize(nextSize)
    // antd clamps the current page when the page size grows.
    const nextTotalPages = Math.max(1, Math.ceil(total / nextSize))
    const nextPage = Math.min(page, nextTotalPages)
    setActivePage(nextPage)
    onShowSizeChange?.(nextPage, nextSize)
    onChange?.(nextPage, nextSize)
  }

  const rangeStart = total === 0 ? 0 : (page - 1) * activeSize + 1
  const rangeEnd = Math.min(page * activeSize, total)

  return (
    <nav
      aria-label="Pagination"
      className={cn(
        PAGINATION_ROOT,
        size === 'small' && 'ui-pagination-mini text-xs',
        align === 'center' && 'justify-center',
        align === 'end' && 'justify-end',
        disabled && 'pointer-events-none opacity-60',
        className
      )}
      style={style}
      {...rest}
    >
      {showTotal ? (
        <span className={PAGINATION_TOTAL}>
          {showTotal(total, [rangeStart, rangeEnd])}
        </span>
      ) : null}

      <button
        type="button"
        aria-label={t('common.previousPage')}
        disabled={page <= 1}
        onClick={() => goTo(page - 1)}
        className={cn(PAGINATION_PREV, FOCUS_RING)}
      >
        <ChevronLeft size={16} aria-hidden="true" />
      </button>

      {simple ? (
        <span className="ui-pagination-simple-pager text-sm tabular-nums">
          {page} / {totalPages}
        </span>
      ) : (
        buildPageItems(page, totalPages).map((item) =>
          typeof item === 'number' ? (
            <button
              key={item}
              type="button"
              aria-current={item === page ? 'page' : undefined}
              aria-label={t('common.pageNumber', { page: item })}
              onClick={() => goTo(item)}
              className={itemClasses(item === page)}
            >
              {item}
            </button>
          ) : (
            <span key={item} className={PAGINATION_ELLIPSIS}>
              ...
            </span>
          )
        )
      )}

      <button
        type="button"
        aria-label={t('common.nextPage')}
        disabled={page >= totalPages}
        onClick={() => goTo(page + 1)}
        className={cn(PAGINATION_NEXT, FOCUS_RING)}
      >
        <ChevronRight size={16} aria-hidden="true" />
      </button>

      {showSizeChanger ? (
        <label className="ui-pagination-options inline-flex items-center gap-1 text-sm">
          <span className="sr-only">{t('common.itemsPerPage')}</span>
          <select
            value={activeSize}
            onChange={(event) => changeSize(Number(event.target.value))}
            className={cn(PAGINATION_SELECT, FOCUS_RING)}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {t('common.pageSizeOption', { count: option })}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {showQuickJumper ? (
        <label className="ui-pagination-jump inline-flex items-center gap-1 text-sm text-[var(--muted-foreground)]">
          Aller à
          <input
            type="number"
            min={1}
            max={totalPages}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return
              goTo(Number(event.currentTarget.value))
              event.currentTarget.value = ''
            }}
            className={cn(PAGINATION_SELECT, 'w-16', FOCUS_RING)}
          />
        </label>
      ) : null}
    </nav>
  )
}

export default Pagination
